import { UserTravelItinerary } from '../api/model/userTravelItinerary';
import { ItineraryItemDetails } from '../api/model/itineraryItemDetails';
import { MealTypes } from './app.types';

/**
 * A single-row itinerary - the server's day trip rule (ItineraryUtil: `IsDayTrip = items.Count == 1`).
 * An absent or empty itinerary is not a day trip.
 */
export function isDayTripItinerary(itinerary?: UserTravelItinerary | null): boolean {
  return (itinerary?.items?.length ?? 0) === 1;
}

/**
 * Mirrors ItineraryUtil.IsSingleDayTrip: a day trip carrying no meal, incidental, catering or overseas value.
 * The server only lets Compliance finalise a TA when every traveller passes this - keep in step.
 */
export function isSingleDayTripItinerary(itinerary?: UserTravelItinerary | null): boolean {
  return isDayTripItinerary(itinerary) && itinerary!.items!.every(item =>
    !item.breakfastValue && !item.lunchValue && !item.dinnerValue && !item.incidentalValue && !item.cateringValue
    && !item.overseasValue);
}

/**
 * The current (highest version) itinerary per traveller for a submission, from a list that holds
 * every version - the server's GetCurrentItinerariesForSubmission rule.
 */
export function latestItinerariesForSubmission(itineraries: UserTravelItinerary[], submissionId?: string | null): UserTravelItinerary[] {
  const latest = new Map<string, UserTravelItinerary>();
  for (const itinerary of itineraries) {
    if (!submissionId || itinerary.submissionId !== submissionId) {
      continue;
    }
    const key = itinerary.userTravelId ?? '';
    if ((itinerary.version ?? 0) >= (latest.get(key)?.version ?? -1)) {
      latest.set(key, itinerary);
    }
  }
  return [...latest.values()];
}

/** A $0 single day trip - the server finalises these on acquittal unless the TA is pre-paid. */
export function isZeroValueDayTripItinerary(itinerary?: UserTravelItinerary | null): boolean {
  return isSingleDayTripItinerary(itinerary) && itinerary!.items!.every(item => !item.dayTripValue);
}

/** True if any item carries an overseas meals & incidentals value - drives the Overseas cost column. */
export function hasOverseasValue(items: ItineraryItemDetails[]): boolean {
  return items.some(item => (item.overseasValue ?? 0) > 0);
}

// CLC-199: these bookings need CTM documentation. Values match the session option lists.
const DOCUMENTED_TRAVEL_MODES = ['Air', 'Hire Car'];
const DOCUMENTED_ACCOMMODATION_TYPES = ['City - Motel'];

/** True when any item is travelled by air or hire car, or stays in a city motel. */
export function requiresDocumentation(items: ItineraryItemDetails[]): boolean {
  return items.some(item =>
    DOCUMENTED_TRAVEL_MODES.includes(item.travelMode ?? '')
    || DOCUMENTED_ACCOMMODATION_TYPES.includes(item.accommodationType ?? ''));
}

/** The claims the server's meal rules enabled on an item, as MealTypes; catering maps to Camping. */
export function enabledMealTypes(enabledMeals?: { [key: string]: boolean } | null): string[] {
  if (!enabledMeals) return [];
  const enabledFields: string[] = [];
  if (enabledMeals['breakfast']) enabledFields.push(MealTypes.Breakfast);
  if (enabledMeals['lunch']) enabledFields.push(MealTypes.Lunch);
  if (enabledMeals['dinner']) enabledFields.push(MealTypes.Dinner);
  if (enabledMeals['catering']) enabledFields.push(MealTypes.Camping);
  if (enabledMeals['overseas']) enabledFields.push(MealTypes.Overseas);
  return enabledFields;
}
